<template>
    <div>
        <limitless-page-header v-if="showAsPage" :title="'Produktval'" :sub-title="page_title"></limitless-page-header>

        <div :class="{ 'content': showAsPage }">
            <div class="card">
                <div v-if="$slots.header" class="card-header header-elements-inline">
                    <slot name="header"></slot>
                </div>
                <div v-if="show" class="card-body">
                    <div v-if="vat" class="alert alert-info">Välj produktutbud för enfunktionsvouchers med {{ vat }}% moms</div>
                    <div v-if="isReadOnly" class="alert alert-info">
                        Kortserien använder produktutbud från
                        <router-link :to="{ name: 'cardseries.edit', params: { id: inheritedProductRangeCardSeriesId } }">
                            serie {{ inheritedProductRangeCardSeriesId }}
                        </router-link>.
                        Redigera källseriens produktutbud istället.
                    </div>
                    <div v-if="canEditCategoryMode" class="border rounded p-3 mb-3">
                        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
                            <div class="font-weight-semibold">Kategori-visning</div>
                            <limitless-button
                                ref="btnsavecategorysettings"
                                type="button"
                                btn-class="btn-light"
                                icon="icon-floppy-disk"
                                :busy="busy_saving_category_settings"
                                @click="saveCategorySettings"
                            >
                                Spara kategorier
                            </limitless-button>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input
                                id="category-mode-manual"
                                v-model="category_settings.category_mode"
                                type="radio"
                                class="custom-control-input"
                                value="manual"
                            >
                            <label class="custom-control-label" for="category-mode-manual">Byt kategori per produkt</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input
                                id="category-mode-merged"
                                v-model="category_settings.category_mode"
                                type="radio"
                                class="custom-control-input"
                                value="merged"
                            >
                            <label class="custom-control-label" for="category-mode-merged">Slå ihop kategorier</label>
                        </div>
                        <div v-if="category_settings.category_mode === 'merged'" class="mt-3">
                            <div
                                v-for="(merge, index) in category_settings.merges"
                                :key="'category-merge-' + index"
                                class="row align-items-start mb-2"
                            >
                                <div class="col-lg-4 form-group mb-2">
                                    <label class="font-weight-semibold">Visa som</label>
                                    <v-select
                                        v-model="merge.target_cat_id"
                                        :options="categories"
                                        :reduce="cat => cat.id"
                                        placeholder="Välj kategori..."
                                    ></v-select>
                                </div>
                                <div class="col-lg-7 form-group mb-2">
                                    <label class="font-weight-semibold">Kategorier som ingår</label>
                                    <v-select
                                        v-model="merge.source_cat_ids"
                                        multiple
                                        :options="categories"
                                        :reduce="cat => cat.id"
                                        placeholder="Välj minst två kategorier..."
                                    ></v-select>
                                </div>
                                <div class="col-lg-1 form-group mb-2 pt-lg-4">
                                    <button type="button" class="btn btn-light" @click="removeCategoryMerge(index)">
                                        <i class="icon-trash"></i>
                                    </button>
                                </div>
                            </div>
                            <button type="button" class="btn btn-light" @click="addCategoryMerge">
                                <i class="icon-plus3 mr-1"></i>
                                Lägg till sammanslagning
                            </button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-lg-3 form-group">
                            <input type="text" v-model="filter.search" class="form-control" placeholder="Sök...">
                        </div>
                        <div class="col-lg-3 form-group">
                            <vue-select
                                v-model="filter.prod_id"
                                multiple
                                taggable
                                placeholder="SKU: 8602,8544..."
                                @search="handleSearch"
                            ></vue-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <supplier-filter
                                v-model="filter.supplier_id"
                                :reduce="(opt) => opt.id"
                                placeholder="-- Leverantör --"
                            />
                        </div>
                        <div class="col-lg-3 form-group">
                            <brand-filter
                                v-model="filter.brand_id"
                                :reduce="(opt) => opt.id"
                                placeholder="-- Varumärke --"
                            />
                        </div>
                        <div class="col-lg-3 form-group">
                            <category-filter
                                v-model="filter.category_id"
                                :reduce="(opt) => opt.id"
                                placeholder="-- Kategori --"
                                show-count
                            />
                        </div>
                        <div class="col-lg-3 form-group">
                            <category-sub-filter
                                v-model="filter.sub_category_id"
                                :reduce="(opt) => opt.id"
                                placeholder="-- Underkategori --"
                                :category-id="filter.category_id"
                            />
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.is_struct"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Strukturartiklar' }))"
                                placeholder="-- Strukturartiklar --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.non_stock"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Beställningsvaror' }))"
                                placeholder="-- Beställningsvaror --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.is_out_of_stock"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Tillfälligt slut' }))"
                                placeholder="-- Tillfälligt slut --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.is_phased_out"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Utgått' }))"
                                placeholder="-- Utgått --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.no_refill"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Ej Refill' }))"
                                placeholder="-- Ej Refill --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <v-select
                                v-model="filter.is_hidden_from_all_cards"
                                :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Gömd från alla kort' }))"
                                placeholder="-- Gömd från alla kort --"
                                :reduce="(opt) => opt.id"
                            ></v-select>
                        </div>
                        <div class="col-lg-3 form-group">
                            <vat-rate-filter
                                v-model="filter.vat_rate"
                                placeholder="-- Momssats --"
                                :reduce="(opt) => opt.id"
                            ></vat-rate-filter>
                        </div>
                        <div class="col-lg-3 form-group">
                            <min-max-slider-filter
                                :min-value="filter.np_min"
                                :max-value="filter.np_max"
                                @update:minValue="filter.np_min = $event"
                                @update:maxValue="filter.np_max = $event"
                                :min="0"
                                :max="9999"
                                label="Inköpspris"
                            ></min-max-slider-filter>
                        </div>
                        <div class="col-auto" style="width: 200px;">
                            <div class="custom-control custom-checkbox mb-1">
                                <input type="checkbox" class="custom-control-input" id="chk-np_incl_freight" v-model="filter.np_incl_freight" :true-value="1" :false-value="0">
                                <label class="custom-control-label" for="chk-np_incl_freight">Filtrera inköpspris + frakt</label>
                            </div>
                            <div class="custom-control custom-checkbox mb-0">
                                <input type="checkbox" class="custom-control-input" id="chk-selected" v-model="filter.show_selected" :true-value="1" :false-value="0">
                                <label class="custom-control-label" for="chk-selected">Visa endast valda</label>
                            </div>
                        </div>
                        <div class="col-auto" style="margin-left: auto;">
                            <limitless-button
                                v-if="cardSerieId > 0 && !isReadOnly"
                                ref="btncopyrange"
                                class="mr-3"
                                btn-class="btn-light"
                                type="button"
                                @click="showModalCopyRange"
                                :busy="busy_copying"
                                icon="icon-copy3"
                                saved-text="KOPIERAT!"
                            >
                                Kopiera produktutbud
                            </limitless-button>
                            <limitless-button v-if="!isReadOnly" ref="btnsaverange1" type="button" @click="saveRange" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                        </div>
                    </div>
                </div>
                <div v-if="show" class="table-responsive" v-bind:class="{ 'loading': busy_loading }">
                    <table class="table table-striped table-xs">
                        <thead>
                            <tr>
                                <th>
                                    <div class="d-flex flex-row justify-content-between">
                                        <div>Bild</div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdID')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>ID</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdID' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdID' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdName')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Produkt</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdName' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdName' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdSupplierID')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Leverantör</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdSupplierID' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdSupplierID' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('CategoryName')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Kategori</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='CategoryName' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='CategoryName' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdVAT')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Moms</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdVAT' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdVAT' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdNetPrice')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>InPris</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdNetPrice' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdNetPrice' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ProdNetPriceInklFreight')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Inkl. Frakt</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ProdNetPriceInklFreight' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ProdNetPriceInklFreight' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('recommended_retail_price')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Rek. förs.pris</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='recommended_retail_price' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='recommended_retail_price' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('Stock_today')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Saldo</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='Stock_today' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='Stock_today' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('Stock_future')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Disp.</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='Stock_future' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='Stock_future' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('has_customs_information')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Tullinfo</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='has_customs_information' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='has_customs_information' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('ItemTypeFlag')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>ProdTyp</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='ItemTypeFlag' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='ItemTypeFlag' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                                <th>
                                    <div @click="setSortBy('selected')" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                                        <div>Vald</div>
                                        <div class="div-table-th-sort">
                                            <i v-if="sort_by_column==='selected' && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                            <i v-else-if="sort_by_column==='selected' && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                            <i v-else class="inactive icon-arrow-resize8"></i>
                                        </div>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody v-if="data.data && data.data.length>0">
                            <tr v-for="(row, r_index) in data.data" :key="'cs-p-' + r_index"
                                v-bind:class="{ 'bg-yellow-200': (range[row.ProdID].flag > 0 && !row.isPhasedOut), 'bg-info-200': (range[row.ProdID].flag < 0 && !row.isPhasedOut), 'bg-danger-200': (row.isPhasedOut) }"
                            >
                                <td>
                                    <div v-if="row.images && row.images[0]">
                                        <img :id="`product-image-${row.ProdID}`" :src="row.images[0].urls['small']" style="width: 60px; height: 60px;">
                                        <b-popover :target="`product-image-${row.ProdID}`" triggers="hover">
                                            <img :src="row.images[0].urls['medium']" :alt="row.ProdID" style="width: 100%;">
                                        </b-popover>
                                    </div>
                                    <div v-else class="border d-flex justify-content-center align-items-center bg-light" style="width: 60px; height: 60px; font-size: 10px;">
                                        Ingen bild
                                    </div>
                                </td>
                                <td>
                                    <router-link
                                        :to="{ name: 'products.edit', params: { id:  row.ProdID } }"
                                        target="_blank"
                                    >
                                        {{ row.ProdID }}
                                    </router-link>
                                </td>
                                <td>
                                    <div style="font-weight: bold;">{{ row.ProdName }}</div>
                                    <div v-if="row.isStruct" class="badge badge-info">STRUKTURARTIKEL</div>
                                    <div v-if="row.isHiddenFromAllCards" class="badge badge-purple">GÖMD FRÅN KORT</div>
                                    <div v-else-if="row.show_on_all_cards" class="badge badge-warning">VISAS PÅ ALLA KORT</div>
                                    <div v-if="row.isPhasedOut === 1" class="badge badge-danger">HAR UTGÅTT</div>
                                    <div v-else>
                                        <div v-if="row.isNonStockItem" class="badge badge-warning">Beställningsvara</div>
                                        <div v-if="row.isOutOfStock === 1">
                                            <div class="badge badge-danger">TILLF. SLUT</div>
                                            <div v-if="row.BackInStockDate && row.BackInStockDate != '0000-00-00'">Åter {{ row.BackInStockDate }}</div>
                                            <div v-else-if="row.no_refill" class="badge badge-purple">EJ REFILL</div>
                                            <div v-else-if="row.autoblockfunction === 'isPhasedOut'" class="badge badge-warning">UTGÅENDE</div>
                                        </div>
                                        <div v-else-if="row.no_refill" class="badge badge-purple">EJ REFILL</div>
                                        <div v-else-if="row.autoblockfunction === 'isPhasedOut'" class="badge badge-warning">UTGÅENDE</div>
                                    </div>
                                </td>
                                <td>{{ (row.SupplierName ? row.SupplierName : '') }}</td>
                                <td>
                                    <v-select
                                        v-if="showProductCategorySelect"
                                        v-model="range[row.ProdID].alt_cat_id"
                                        :placeholder="row.DefaultCategoryName"
                                        :options="categories"
                                        :reduce="cat => cat.id"
                                        style="min-width: 200px;"
                                        :disabled="isReadOnly"
                                        @input="(val) => { if (val) { range[row.ProdID].flag = product_range_flags['CUSTOM'].value }}"
                                    ></v-select>
                                    <span v-else>{{ row.CategoryName }}</span>
                                </td>
                                <td :class="{ 'bg-danger': vat && vat != row.ProdVAT && range[row.ProdID].flag != product_range_flags['NOT_CHOOSEN'].value }">{{ row.ProdVAT }}%</td>
                                <td>{{ formatCurrency(row.ProdNetPrice) }}</td>
                                <td>{{ formatCurrency(row.ProdNetPriceInklFreight) }}</td>
                                <td>{{ row.recommended_retail_price ? formatCurrency(row.recommended_retail_price) : '' }}</td>
                                <td>{{ row.Stock_today }}</td>
                                <td>{{ row.Stock_future }}</td>
                                <td>
                                    <div v-if="row.has_customs_information" class="badge badge-success">JA</div>
                                    <div v-else class="badge badge-danger">NEJ</div>
                                </td>
                                <td>
                                    <div class="form-group my-0 pt-1">
                                        <div class="custom-control custom-radio mb-1">
                                            <input
                                                type="radio"
                                                class="custom-control-input"
                                                v-model="range[row.ProdID].flag"
                                                :id="'cs-p-' + r_index + '-NOT_CHOOSEN'"
                                                :value="product_range_flags['NOT_CHOOSEN'].value"
                                                :disabled="isReadOnly || (range[row.ProdID].series_flag !== product_range_flags['NOT_CHOOSEN'].value && cardOrderId > 0)"
                                            >
                                            <label class="custom-control-label" :for="'cs-p-' + r_index + '-NOT_CHOOSEN'">{{ product_range_flags['NOT_CHOOSEN'].name }}</label>
                                        </div>
                                        <div v-if="cardSerieId > 0" class="custom-control custom-radio mb-1">
                                            <input
                                                type="radio"
                                                class="custom-control-input"
                                                v-model="range[row.ProdID].flag"
                                                :id="'cs-p-' + r_index + '-STANDARD'"
                                                :value="product_range_flags['STANDARD'].value"
                                                :disabled="isReadOnly || (range[row.ProdID].series_flag !== product_range_flags['STANDARD'].value && cardOrderId > 0)"
                                            >
                                            <label class="custom-control-label" :for="'cs-p-' + r_index + '-STANDARD'">{{ product_range_flags['STANDARD'].name }}</label>
                                        </div>
        <!--                                <div v-if="cardSerieId > 0" class="custom-control custom-radio mb-1">-->
        <!--                                    <input-->
        <!--                                        type="radio"-->
        <!--                                        class="custom-control-input"-->
        <!--                                        v-model="range[row.ProdID].flag"-->
        <!--                                        :id="'cs-p-' + r_index + '-EXTRA'"-->
        <!--                                        :value="product_range_flags['EXTRA'].value"-->
        <!--                                        :disabled="range[row.ProdID].series_flag !== product_range_flags['EXTRA'].value && cardOrderId > 0"-->
        <!--                                    >-->
        <!--                                    <label class="custom-control-label" :for="'cs-p-' + r_index + '-EXTRA'">{{ product_range_flags['EXTRA'].name }}</label>-->
        <!--                                </div>-->
                                        <div v-if="cardSerieId <= 0 && cardOrderId <= 0" class="custom-control custom-radio mb-1">
                                            <input type="radio" class="custom-control-input" v-model="range[row.ProdID].flag" :id="'cs-p-' + r_index + '-LEFTOVER'" :value="product_range_flags['LEFTOVER'].value" :disabled="isReadOnly">
                                            <label class="custom-control-label" :for="'cs-p-' + r_index + '-LEFTOVER'">{{ product_range_flags['LEFTOVER'].name }}</label>
                                        </div>
                                        <div v-if="cardSerieId > 0 && cardOrderId > 0" class="custom-control custom-radio mb-1">
                                            <input
                                                type="radio"
                                                class="custom-control-input"
                                                v-model="range[row.ProdID].flag"
                                                :id="'cs-p-' + r_index + '-CUSTOM'"
                                                :value="product_range_flags['CUSTOM'].value"
                                                :disabled="isReadOnly"
                                            >
                                            <label class="custom-control-label" :for="'cs-p-' + r_index + '-CUSTOM'">{{ product_range_flags['CUSTOM'].name }}</label>
                                        </div>
                                        <div v-if="cardSerieId > 0 && cardOrderId > 0" class="custom-control custom-radio mb-1">
                                            <input
                                                type="radio"
                                                class="custom-control-input"
                                                v-model="range[row.ProdID].flag"
                                                :id="'cs-p-' + r_index + '-BLOCKED'"
                                                :value="-1"
                                                :disabled="isReadOnly"
                                            >
                                            <label class="custom-control-label" :for="'cs-p-' + r_index + '-BLOCKED'">BLOCKERA</label>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-center">
                                    <div v-if="row.selected" class="badge badge-success">JA</div>
                                    <div v-else class="badge badge-danger">NEJ</div>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr>
                                <td colspan="11">
                                    <div class="p-4 text-center"><i>Ingen data...</i></div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="show" class="card-footer bg-white">
                    <div v-if="data.meta" class="row">
                        <div class="col-12 col-md-4 text-center text-md-left">
                            <p class="mb-0">Visar {{ data.meta.from }} - {{ data.meta.to }} av {{ data.meta.total }} rader</p>
                        </div>
                        <div class="col-12 col-md-4 text-center text-md-right mb-0">
                            <nav>
                                <ul class="pagination justify-content-center">
                                    <li class="page-item" v-bind:class="{disabled:page<=1}">
                                        <a class="page-link" @click.prevent="btnSearchPreviousPage" href="#">←   Föregående</a>
                                    </li>
                                    <li class="page-item active">
                                        <span class="page-link">Sida {{ page }} av {{ data.meta.last_page }}</span>
                                    </li>
                                    <li class="page-item" v-bind:class="{disabled:page>=data.meta.last_page}">
                                        <a class="page-link" @click.prevent="btnSearchNextPage" href="#">Nästa   →</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="col-12 col-md-4 text-center text-md-right">
                            <limitless-button v-if="!isReadOnly" ref="btnsaverange2" type="button" @click="saveRange" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                        </div>
                    </div>
                </div>

                <b-modal id="modal-copy-range" title="Kopiera produktutbud från annan kortserie" @ok="copyRange" ok-title="Kopiera" cancel-title="Avbryt" cancel-variant="light" :ok-disabled="!(copy_range_from_serie)">
                    <div class="form-group">
                        <label class="font-weight-semibold">Kortserie</label>
                        <v-select
                            :options="cardseries"
                            :get-option-label="(s) => s.CardSeriesID + ': ' + s.SeriesName"
                            placeholder="Välj kortserie..."
                            v-model="copy_range_from_serie"
                            :reduce="(opt) => opt.CardSeriesID"
                        ></v-select>
                    </div>
                    <p>När du valt en kortserie att kopiera produkturvalet ifrån här ovan kommer befintligt urval på <strong>denna</strong> kortserie att skrivas över.</p>
                </b-modal>
            </div>
        </div>
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader.vue';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import apiClient from '@services/ApiClient';
import vSelect from 'vue-select';
import { debounce } from 'lodash';
import { formatCurrency } from '@services/Helpers';
import BackendRepository from '@services/repositories/BackendRepository';
import CategoryFilter from '@components/Filters/CategoryFilter.vue';
import SupplierFilter from '@components/Filters/SupplierFilter.vue';
import MinMaxSliderFilter from '@components/Filters/MinMaxSliderFilter.vue';
import BrandFilter from '@components/Filters/BrandFilter.vue';
import VueSelect from '@components/Wrappers/VueSelect.vue';
import CategorySubFilter from '@components/Filters/CategorySubFilter.vue';
import VatRateFilter from '@components/Filters/VatRateFilter.vue';

export default {
    name: 'ProductRangeSelection',
    components: {
        VatRateFilter,
        CategorySubFilter,
        VueSelect,
        BrandFilter,
        MinMaxSliderFilter,
        SupplierFilter,
        CategoryFilter,
        LimitlessPageHeader,
        LimitlessButton,
        vSelect,
    },
    props: {
        cardSerieId: {
            type: [Number, String],
            required: true,
        },
        cardOrderId: {
            type: [Number, String],
            default: 0,
        },
        show: {
            type: Boolean,
            default: true,
        },
        autoload: {
            type: Boolean,
            default: true,
        },
        perPage: {
            type: Number,
            default: 100,
        },
        sortBy: {
            type: String,
            default: '',
        },
        sortOrder: {
            type: String,
            default: '',
        },
        showAsPage: {
            type: Boolean,
            default: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        productRangeCardSeriesId: {
            type: [Number, String],
            default: null,
        },
    },
    watch: {
        filter: {
            deep: true,
            handler() {
                this.page = 1;
                this.get_products();
            },
        },
    },
    data: () => ({
        busy_loading: false,
        busy_saving: false,
        busy_copying: false,
        busy_saving_category_settings: false,
        cardorder: null,
        range: {},
        cardseries: [],
        categories: [],
        category_settings: {
            category_mode: 'manual',
            merges: [],
        },
        original_category_mode: 'manual',
        product_range_flags: [],
        data: [],
        filter: {
            np_incl_freight: 0,
            show_selected: 0,
            search: '',
            supplier_id: null,
            brand_id: null,
            category_id: null,
            sub_category_id: null,
            is_struct: null,
            is_out_of_stock: null,
            is_phased_out: 0,
            no_refill: null,
            is_hidden_from_all_cards: null,
            non_stock: null,
            is_news: null,
            vat_rate: null,
            np_min: null,
            np_max: null,
            prod_id: [],
        },
        page: 1,
        sort_by_column: '',
        sort_by_order: '',
        sort_clicktimer: null,
        sort_clicks: 0,
        get_products: debounce(function getdata() {
            const vm = this;
            vm.busy_loading = true;
            apiClient.get(`/vue/product_range/products/${this.cardSerieId}/${this.cardOrderId}?page=${this.page}&per_page=${this.perPage}&sort_by_column=${this.sort_by_column}&sort_by_order=${this.sort_by_order}`, { params: this.filter })
                .then((response) => {
                    vm.data = response.data;
                })
                .finally(() => {
                    vm.busy_loading = false;
                });
        }, 250),
        copy_range_from_serie: null,
        status_options: [
            {
                id: null,
                label: 'Inkl. ',
            },
            {
                id: 1,
                label: 'Endast',
            },
            {
                id: 0,
                label: 'Dölj',
            },
        ],
    }),
    mounted() {
        if (this.autoload) {
            this.load();
        }
    },
    created() {
        this.sort_by_column = this.sortBy;
        this.sort_by_order = this.sortOrder;
    },
    destroyed() {
        if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
    },
    methods: {
        formatCurrency,
        load() {
            return new Promise((resolve) => {
                this.init();
                this.getRange();
                this.get_products();
                resolve();
            });
        },
        init() {
            BackendRepository.getProductRangeCategories().then((res) => {
                this.categories = res;
            });
            BackendRepository.getProductRangeFlags().then((res) => {
                this.product_range_flags = res;
            });
            apiClient.get('/vue/cardseries?show_hidden=1&only_series=1&per_page=10000')
                .then((response) => {
                    this.cardseries = response.data.data;
                });
            if (parseInt(this.cardSerieId, 10) > 0) {
                this.getCategorySettings();
            }
            if (this.cardOrderId) {
                apiClient.get('/vue/cardorders/' + this.cardOrderId)
                    .then((response) => {
                        this.cardorder = response.data.card_order;
                    });
            }
        },
        btnSearchPreviousPage() {
            this.page -= 1;
            this.get_products();
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        btnSearchNextPage() {
            this.page += 1;
            this.get_products();
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        setSortBy(column) {
            if (this.sort_by_column === column) {
                this.sort_clicks += 1;
                if (this.sort_clicks > 1) {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_by_column = '';
                    this.sort_by_order = '';
                    this.sort_clicks = 0;
                } else {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_clicktimer = setTimeout(() => {
                        this.sort_clicks = 0;
                    }, 2000);
                    if (this.sort_by_order === 'asc') {
                        this.sort_by_order = 'desc';
                    } else {
                        this.sort_by_order = 'asc';
                    }
                }
            } else {
                if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                this.sort_by_column = column;
                this.sort_by_order = 'asc';
                this.sort_clicks = 0;
            }
            this.page = 1;
            this.get_products(true);
        },
        rowClick(row) {
            if (this.has_row_click_listener) {
                this.$emit('rowclick', row);
            }
        },
        getRange() {
            apiClient.get(`/vue/product_range/${this.cardSerieId}/${this.cardOrderId}`)
                .then((response) => {
                    this.range = response.data;
                });
        },
        getCategorySettings() {
            apiClient.get(`/vue/product_range/category_settings/${this.cardSerieId}`)
                .then((response) => {
                    this.category_settings = {
                        category_mode: response.data.category_mode || 'manual',
                        merges: response.data.merges || [],
                    };
                    this.original_category_mode = this.category_settings.category_mode;
                });
        },
        addCategoryMerge() {
            this.category_settings.merges.push({
                target_cat_id: null,
                source_cat_ids: [],
            });
        },
        removeCategoryMerge(index) {
            this.category_settings.merges.splice(index, 1);
        },
        async saveCategorySettings() {
            if (this.category_settings.category_mode !== this.original_category_mode) {
                const result = await this.$swal({
                    icon: 'warning',
                    title: 'Byt kategoriläge?',
                    text: 'Befintliga kategoriändringar för det andra läget tas bort när du sparar.',
                    showCancelButton: true,
                    confirmButtonText: 'Spara',
                    cancelButtonText: 'Avbryt',
                    reverseButtons: true,
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            this.busy_saving_category_settings = true;
            apiClient.post(`/vue/product_range/category_settings/${this.cardSerieId}`, this.category_settings)
                .then((response) => {
                    this.category_settings = {
                        category_mode: response.data.category_mode || 'manual',
                        merges: response.data.merges || [],
                    };
                    this.original_category_mode = this.category_settings.category_mode;
                    if (this.$refs.btnsavecategorysettings) {
                        this.$refs.btnsavecategorysettings.triggerSaved();
                    }
                    this.getRange();
                    this.get_products();
                })
                .catch((error) => {
                    if (this.$refs.btnsavecategorysettings) {
                        this.$refs.btnsavecategorysettings.triggerFailed();
                    }
                    if (error.response && error.response.data && (error.response.data.message || error.response.data.error)) {
                        this.$swal({
                            icon: 'warning',
                            title: 'Kunde inte spara',
                            text: error.response.data.message || error.response.data.error,
                            reverseButtons: true,
                            confirmButtonText: 'OK',
                        });
                    }
                })
                .finally(() => {
                    this.busy_saving_category_settings = false;
                });
        },
        saveRange() {
            this.busy_saving = true;
            apiClient.post(`/vue/product_range/${this.cardSerieId}/${this.cardOrderId}`, { range: this.range })
                .then((response) => {
                    this.range = response.data;
                    this.busy_saving = false;
                    this.$refs.btnsaverange1.triggerSaved();
                    this.$refs.btnsaverange2.triggerSaved();
                    this.get_products();
                })
                .catch(() => {
                    this.$refs.btnsaverange1.triggerFailed();
                    this.$refs.btnsaverange2.triggerFailed();
                });
        },
        showModalCopyRange() {
            this.$bvModal.show('modal-copy-range');
        },
        copyRange() {
            this.busy_copying = true;
            apiClient.post(`/vue/product_range/${this.cardSerieId}/${this.cardOrderId}/copy`, { from_serie: this.copy_range_from_serie })
                .then((response) => {
                    this.range = response.data;
                    this.get_products();
                    this.$refs.btncopyrange.triggerSaved();
                })
                .catch(() => {
                    this.$refs.btncopyrange.triggerFailed();
                }).finally(() => {
                    this.busy_copying = false;
                });
        },
        handleSearch(search) {
            if (typeof search === 'string' || search instanceof String) {
                const splitOnComma = search.split(',').length > 1;
                if (splitOnComma) {
                    const selected = search.split(',')
                        .map((sku) => sku.trim());
                    this.filter.prod_id = [...this.filter.prod_id, ...selected];
                }
            }
        },
    },
    computed: {
        has_row_click_listener() {
            return this.$listeners && this.$listeners.rowclick;
        },
        page_title() {
            let title = '';

            if (this.cardSerieId) {
                title += 'Kortserie #' + this.cardSerieId;
                if (this.cardserie) {
                    title += ' - ' + this.cardserie.SeriesName;
                }
            }
            if (this.cardOrderId) {
                title += ', Order #' + this.cardOrderId;
            }

            return title;
        },
        cardserie() {
            return this.cardseries ? this.cardseries.find((s) => parseInt(s.CardSeriesID, 10) === parseInt(this.cardSerieId, 10)) : null;
        },
        inheritedProductRangeCardSeriesId() {
            return this.productRangeCardSeriesId || (this.cardserie ? this.cardserie.ProductRangeCardSeriesID : null);
        },
        isReadOnly() {
            return this.readOnly || (parseInt(this.cardOrderId, 10) <= 0 && parseInt(this.inheritedProductRangeCardSeriesId, 10) > 0);
        },
        canEditCategoryMode() {
            return parseInt(this.cardSerieId, 10) > 0 && parseInt(this.cardOrderId, 10) === 0 && !this.isReadOnly;
        },
        showProductCategorySelect() {
            return parseInt(this.cardSerieId, 10) > 0 && this.category_settings.category_mode !== 'merged';
        },
        vat() {
            if (this.cardorder) {
                const orderItem = this.cardorder.items.find((s) => parseInt(s.CardSeriesID, 10) === parseInt(this.cardSerieId, 10));
                return ((orderItem && orderItem.voucher_type === 1) ? orderItem.VAT : null);
            }
            if (this.cardserie) {
                return this.cardserie.voucher_type === 1 ? this.cardserie.VAT : null;
            }
            return null;
        },
    },
};
</script>
<style scoped>
.table-responsive.loading {
    position: relative;
}
.table-responsive.loading:before {
    content: 'Laddar...';
    display: block;
    position: absolute;
    text-align: center;
    padding: 50px 10px;
    height: 100%;
    width: 100%;
    background-color: rgba(255,255,255, .8);
    z-index: 5;
    font-size: 1rem;
    font-weight: bold;
}
</style>
